/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem } from '@msdyn365-commerce-modules/address';
import { IAddressSelectItem } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressViewProps } from './lds-checkout-shipping-address-4';
import { ILdsAddressSelectProps, ILdsAddressAddUpdateProps } from './lds-checkout-shipping-address-4';
import { AfricaCheckout } from './components/customShippingSections/AfricaShipping';
import { BrazilCheckout } from './components/customShippingSections/BrazilShipping';
import { PeruCheckout } from './components/customShippingSections/PeruShipping';
import { UruguayCheckout } from './components/customShippingSections/UruguayShipping';
import { ChileCheckout } from './components/customShippingSections/ChileShipping';
import { Waiting } from '@msdyn365-commerce-modules/utilities';
import { stateCheckListChl } from './components/countryFilterLists.tsx/Chile';
import { validateAddressFormatAsync, stateNameTranslationAsync, countryStateTranslationAsync } from '../../actions/DataActionExtension.g';

import {
    stateCheckList,
    stateCheckListKo,
    stateCheckListMixed,
    // ArgState,
    cityCheck
} from '../../helpers/utilities/state-address-utilities';

//@ts-ignore
import { Primary, Secondary, Ghost } from '@churchofjesuschrist/eden-buttons';

export interface ILdsAddressShowProps extends IAddressShowProps {
    defaultCountryRegionId?: any;
    context?: any;
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<ILdsAddressShowProps> = props => {
    const [stateName, setStateName] = React.useState('');
    const [filteredList, setFilteredList] = React.useState([]);
    const translationCache = React.useRef(new Map());

    const { AddressDetail, items, defaultCountryRegionId, context } = props;
    let filteredItems: any;

    const getStateTranslation = async (stateName: any, countryId: any) => {
        const ctx = { callerContext: context.actionContext };

        // Check if translation is already cached
        const cacheKey = `${countryId[0].description.props.value}-${stateName[0].description.props.value}`;
        if (translationCache.current.has(cacheKey)) {
            setStateName(translationCache.current.get(cacheKey));
            return;
        }

        let translation;
        if (stateName && stateName[0] && countryId && countryId[0]) {
            translation = await stateNameTranslationAsync(
                ctx,
                countryId[0].description.props.value,
                stateName[0].description.props.value,
                context.request.locale
            );
        }

        if (translation && translation.DisplayName) {
            setStateName(translation.DisplayName);
            // Cache the translation
            translationCache.current.set(cacheKey, translation.DisplayName);
        }
    };

    React.useEffect(() => {
        let filteredStateList: any;
        const findState = items.filter((item: any) => {
            return item.key === 'State';
        });
        const findCountry = items.filter((item: any) => {
            return item.key === 'ThreeLetterISORegionName';
        });

        void getStateTranslation(findState, findCountry);
        if (findState && findState[0] && findCountry && findCountry[0]) {
            // @ts-ignore
            if (defaultCountryRegionId === 'KOR' || findCountry[0].description.props.value === 'KOR') {
                // @ts-ignore
                filteredStateList = stateCheckListMixed.some(item => item.value === findState[0].description.props.value);
                if (filteredStateList === true) {
                    filteredItems = items.filter((item: any) => {
                        return item.key !== 'City';
                    });
                    setFilteredList(filteredItems);
                }
                // @ts-ignore
            } else if (defaultCountryRegionId === 'VNM' || findCountry[0].description.props.value === 'VNM') {
                // @ts-ignore
                filteredStateList = filteredStateList = cityCheck.includes(findState[0].description.props.value);

                if (filteredStateList === true) {
                    filteredItems = items.filter((item: any) => {
                        return item.key !== 'City';
                    });
                    setFilteredList(filteredItems);
                }
            }
        }
    }, [props.items]);

    return (
        <Node {...AddressDetail}>
            {filteredList && filteredList.length > 0
                ? filteredList.map((item: IAddressShowItem) => {
                      if (defaultCountryRegionId === 'GHA' || defaultCountryRegionId === 'CIV' || defaultCountryRegionId === 'NGA') {
                          return (
                              <div key={item.key} className={`show-address-${item.key}`}>
                                  {item.description}
                              </div>
                          );
                      } else {
                          if (item.key === 'State' && stateName) {
                              return (
                                  <span
                                      key={item.key}
                                      className='msc-address-detail__item msc-address-detail__item-state msc-address-detail__item-newline'
                                  >
                                      {stateName}
                                  </span>
                              );
                          } else {
                              return <>{item.description}</>;
                          }
                      }
                  })
                : items.map((item: IAddressShowItem) => {
                      if (defaultCountryRegionId === 'GHA' || defaultCountryRegionId === 'CIV' || defaultCountryRegionId === 'NGA') {
                          return (
                              <div key={item.key} className={`show-address-${item.key}`}>
                                  {item.description}
                              </div>
                          );
                      } else {
                          if (item.key === 'State' && stateName) {
                              return (
                                  <span
                                      key={item.key}
                                      className='msc-address-detail__item msc-address-detail__item-state msc-address-detail__item-newline'
                                  >
                                      {stateName}
                                  </span>
                              );
                          } else {
                              return <>{item.description}</>;
                          }
                      }
                  })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<ILdsAddressSelectProps> = props => {
    const [isDisabled, setIsDisabled] = React.useState(true);
    const [recordId, setRecordId] = React.useState<any>();
    const [validResults, setValidResults] = React.useState([]);

    React.useEffect(() => {
        const isItemSelected = items.filter((item: IAddressSelectItem) => {
            // @ts-ignore
            return item.input.props.additionalAddributes.checked;
        });
        if (isItemSelected.length !== 0) {
            setRecordId(isItemSelected[0].key);
            setIsDisabled(false);
        }
    }, []);

    // const { SelectAddress, addButton, items, isShowSaveButton, saveButton, isShowCancelButton, cancelButton, resources } = props;
    const { SelectAddress, addButton, items, saveButton, cancelButton, context } = props;

    React.useEffect(() => {
        const ctx = { callerContext: props.context.actionContext };
        const filterAsyncResults = async (array: any) => {
            const results: any = [];

            for (const obj of array) {
                const result = await validateAddressFormatAsync(ctx, obj.key);
                const addressCountry = obj.showItems.items.filter((item: any) => {
                    return item.key === 'ThreeLetterISORegionName';
                });

                if (
                    result &&
                    addressCountry &&
                    addressCountry[0] &&
                    addressCountry[0].description &&
                    addressCountry[0].description.props &&
                    addressCountry[0].description.props.value === 'URY' &&
                    result.StateValid === true &&
                    result.CityValid === true &&
                    result.StreetNumberValid === true &&
                    result.CountryRegionValid === true
                ) {
                    results.push(obj);
                }
                if (
                    result &&
                    addressCountry &&
                    addressCountry[0] &&
                    addressCountry[0].description &&
                    addressCountry[0].description.props &&
                    addressCountry[0].description.props.value === 'KOR' &&
                    result.StateValid === true &&
                    result.StreetNumberValid === true &&
                    result.CountryRegionValid === true
                ) {
                    results.push(obj);
                } else if (result && result.AddressValid === true) {
                    results.push(obj);
                }
            }

            setValidResults(results);
        };

        void filterAsyncResults(items);
    }, []);

    const onSaveFunction = async (functionOnClick: any) => {
        const ctx = { callerContext: props.context.actionContext };

        await validateAddressFormatAsync(ctx, Number(recordId)).catch(err => {
            console.log(err);
        });
        functionOnClick();
    };

    return (
        <Node {...SelectAddress}>
            {/* {addButton} */}
            <Secondary
                style={{ width: '100%', marginBottom: validResults && validResults.length === 0 ? '20px' : '0px' }}
                // style={{ width: '100%' }}
                //@ts-ignore
                onClick={() => addButton.props.children.props.onClick()}
            >
                {/* @ts-ignore */}
                {addButton.props.children.props.text || addButton.props.text || 'Add address'}
            </Secondary>
            {validResults
                ? validResults.map((item: IAddressSelectItem) => {
                      const SelectItem = item.SelectItem;
                      const customOnChange = (e: any) => {
                          if (isDisabled === true) {
                              setIsDisabled(false);
                          }
                          if (e.target.value) {
                              setRecordId(e.target.value);
                          }
                          // @ts-ignore
                          item.input.props.onChange(e);
                      };
                      return (
                          <Node {...SelectItem} key={item.key}>
                              <input
                                  type='radio'
                                  // @ts-ignore
                                  onChange={e => customOnChange(e)}
                                  checked={
                                      // @ts-ignore
                                      item.input.props.value === JSON.stringify(recordId)
                                          ? true
                                          : // @ts-ignore
                                          item.input.props.value === recordId
                                          ? true
                                          : false
                                  }
                                  // @ts-ignore
                                  value={item.input.props.value}
                                  // @ts-ignore
                                  className='msc-address-select__input-radio'
                                  style={{
                                      height: '18px',
                                      width: '18px'
                                  }}
                                  key={item.key}
                                  // @ts-ignore
                                  aria-setsize={item.input.props.additionalAddributes['aria-setsize']}
                              />
                              <AddressShow {...item.showItems} context={context} />
                          </Node>
                      );
                  })
                : items.map((item: IAddressSelectItem) => {
                      const SelectItem = item.SelectItem;
                      const customOnChange = (e: any) => {
                          if (isDisabled === true) {
                              setIsDisabled(false);
                          }
                          if (e.target.value) {
                              setRecordId(e.target.value);
                          }
                          // @ts-ignore
                          item.input.props.onChange(e);
                      };
                      return (
                          <Node {...SelectItem} key={item.key}>
                              <input
                                  type='radio'
                                  // @ts-ignore
                                  onChange={e => customOnChange(e)}
                                  // @ts-ignore
                                  checked={item.input.props.additionalAddributes.checked || false}
                                  // @ts-ignore
                                  value={item.input.props.value}
                                  // @ts-ignore
                                  className='msc-address-select__input-radio'
                                  style={{
                                      height: '18px',
                                      width: '18px'
                                  }}
                                  key={item.key}
                                  // @ts-ignore
                                  aria-setsize={item.input.props.additionalAddributes['aria-setsize']}
                              />
                              <AddressShow {...item.showItems} context={context} />
                          </Node>
                      );
                  })}

            <div className='custom-button-group'>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {cancelButton && (
                        // @ts-ignore
                        <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                            {/* @ts-ignore */}
                            {cancelButton.props.text}
                        </Ghost>
                    )}
                </div>
                <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {saveButton && (
                        // @ts-ignore
                        <Secondary style={{ width: '100%' }} onClick={() => onSaveFunction(saveButton.props.onClick)} disabled={isDisabled}>
                            {/* <Secondary style={{ width: '100%' }} onClick={() => saveButton.props.onClick()} disabled={isDisabled}> */}
                            {/* @ts-ignore */}
                            {saveButton.props.text}
                        </Secondary>
                    )}
                </div>
            </div>
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @param param0.loading - isLoading.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<ILdsAddressAddUpdateProps> = props => {
    const {
        AddressForm,
        heading,
        items,
        hasError,
        error,
        saveButton,
        cancelButton,
        resources,
        defaultCountryRegionId,
        context,
        showSmarty,
        smartyFailed,
        smartyAddress,
        userAddress,
        loading
    } = props;
    const [inputAddress, setInputAddress] = React.useState<any>({});
    const [isVisible, setIsVisible] = React.useState<any>(false);
    const [isDisabled, setIsDisabled] = React.useState<any>(true);
    const [unitName, setUnitName] = React.useState<any>('');
    const [stakeNameAddress, setStakeNameAddress] = React.useState<any>('');
    const [disabled, setDisabled] = React.useState<any>(true);
    const [hideCity, setHideCity] = React.useState<any>(false);
    const [updatedStateList, setUpdatedStateList] = React.useState<any>([]);

    const updateNames = (array1: any, array2: any) => {
        return array1.map((item1: any) => {
            const match = array2.find((item2: any) => {
                return item2.StateId === item1.key;
            });
            if (match && match.LongName !== '') {
                // return { ...item1, value: match.DisplayName };
                return { ...item1, value: match.LongName };
            }
            return item1;
        });
    };

    const stateListTranslation = async (stateList: any) => {
        let translatedStateList;
        const ctx = { callerContext: context.actionContext };

        // const response = await countryStateTranslationAsync(ctx, defaultCountryRegionId, props.context.request.locale);
        const response = await countryStateTranslationAsync(ctx, defaultCountryRegionId, props.context.request.locale).catch((err: any) => {
            return console.log(err);
        });

        if (stateList && stateList.length !== 0) {
            translatedStateList = updateNames(stateList, response);
        }

        if (translatedStateList && translatedStateList.length > 0) {
            translatedStateList.filter((option: any) => {
                return option.value !== '';
            });
            setUpdatedStateList(translatedStateList);
        } else if (updatedStateList.length !== 0) {
            setUpdatedStateList(stateList);
        }
    };

    React.useEffect(() => {
        let stateList: any;
        let response: any;
        let countryId: any;
        if (props && props.items && props.items.length > 0) {
            response = props.items.filter((item: any) => {
                return item.key === 'State';
            });
            countryId = props.items.filter((item: any) => {
                return item.key === 'ThreeLetterISORegionName';
            });
        }
        if (response && response[0] && response[0].input && response[0].input.props && response[0].input.props.displayData) {
            stateList = response[0].input.props.displayData.filter((option: any) => {
                return option.value !== '';
            });
        }
        if (updatedStateList.length === 0 && countryId[0].input.props.value !== 'URY') {
            void stateListTranslation(stateList);
        }
    }, [props]);

    React.useEffect(() => {
        if (smartyFailed) {
            // @ts-ignore
            saveButton.props.onClick(userAddress);
        }
    }, [smartyFailed]);

    const filteredItems = items.filter((item: any) => {
        if (hideCity) {
            return item.key !== 'Phone' && item.key !== 'City';
        } else {
            return item.key !== 'Phone';
        }
    });

    if (defaultCountryRegionId === 'MEX') {
        const districtIndex = filteredItems.findIndex(item => item.key === 'DistrictName');

        const street2Index = filteredItems.findIndex(item => item.key === 'Street_2');

        if (districtIndex !== -1 && street2Index !== -1) {
            const [removedDistrict] = filteredItems.splice(districtIndex, 1);

            filteredItems.splice(street2Index, 0, removedDistrict);
        }
    }

    const customSetAddress = (address: any) => {
        setInputAddress(address);
    };

    React.useEffect(() => {
        //LDS DEV NOTE: Here we are adding a form of validation to not allow users to continue in address setup as OOTB code does not have all the correct requirements the
        //Church needs to have a valid address.
        const canSubmit: any = [];
        if (defaultCountryRegionId === 'MEX') {
            filteredItems.map((item: any) => {
                if (
                    item.key === 'Name' ||
                    item.key === 'DistrictName' ||
                    item.key === 'Street' ||
                    item.key === 'ZipCode' ||
                    item.key === 'City'
                ) {
                    return canSubmit.push(item.input.props.value !== undefined && item.input.props.value !== '' ? true : false);
                } else if (item.key === 'State') {
                    return canSubmit.push(item.input.props.value !== '' ? true : false);
                }
            });
            if (canSubmit.includes(false)) {
                setDisabled(true);
            } else if (!canSubmit.includes(false) && disabled === true) {
                setDisabled(false);
            }
        } else if (defaultCountryRegionId === 'AUS') {
            filteredItems.map((item: any) => {
                if (item.key === 'Name' || item.key === 'Street' || item.key === 'ZipCode' || item.key === 'City') {
                    return canSubmit.push(item.input.props.value !== undefined && item.input.props.value !== '' ? true : false);
                } else if (item.key === 'State') {
                    return canSubmit.push(item.input.props.value !== '' ? true : false);
                }
            });
            if (canSubmit.includes(false)) {
                setDisabled(true);
            } else if (!canSubmit.includes(false) && disabled === true) {
                setDisabled(false);
            }
        } else if (defaultCountryRegionId === 'PAN') {
            filteredItems.map((item: any) => {
                if (item.key === 'Name' || item.key === 'Street' || item.key === 'ZipCode' || item.key === 'City') {
                    return canSubmit.push(item.input.props.value !== undefined && item.input.props.value !== '' ? true : false);
                } else if (item.key === 'State') {
                    return canSubmit.push(item.input.props.value !== '' ? true : false);
                }
            });
            if (canSubmit.includes(false)) {
                setDisabled(true);
            } else if (!canSubmit.includes(false) && disabled === true) {
                setDisabled(false);
            }
        } else if (defaultCountryRegionId === 'PER') {
            filteredItems.map((item: any) => {
                if (item.key === 'Name' || item.key === 'Street' || item.key === 'ZipCode') {
                    return canSubmit.push(item.input.props.value !== undefined && item.input.props.value !== '' ? true : false);
                } else if (item.key === 'State') {
                    return canSubmit.push(item.input.props.value !== '' ? true : false);
                }
            });
            if (canSubmit.includes(false)) {
                setDisabled(true);
            } else if (!canSubmit.includes(false) && disabled === true) {
                setDisabled(false);
            }
        }
    }, [props]);

    const customKoreaStateOnChange = (functionToExecute: any, data: any) => {
        if (cityCheck.includes(data.target.value)) {
            setHideCity(true);
        } else {
            setHideCity(false);
        }
        functionToExecute(data);
    };

    if (loading) {
        return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Waiting />
            </div>
        );
    } else if (showSmarty) {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                {smartyAddress && smartyAddress.street && (
                    <div>
                        <h4 style={{ marginBottom: '10px' }}>{resources.smartyStreetAddressHeader}</h4>
                        <div>{`${smartyAddress.street} ${smartyAddress.street2}`}</div>
                        <div>{`${smartyAddress.city}, ${smartyAddress.state} ${smartyAddress.zipcode}${smartyAddress.plusfour ? '-' : ''}${
                            smartyAddress.plusfour
                        }`}</div>
                        <div style={{ marginBottom: '10px' }}>{`${smartyAddress.country}`}</div>
                        {/* @ts-ignore */}
                        <Secondary onClick={() => saveButton.props.onClick(smartyAddress)}>
                            {resources.smartyStreetsAddressButton}
                        </Secondary>
                    </div>
                )}
                <div>
                    <h4 style={{ marginBottom: '10px' }}>{resources.userShippingAddressHeader}</h4>
                    <div>{`${userAddress.Street} ${userAddress.Street_2 || ''}`}</div>
                    <div>{`${userAddress.City}, ${userAddress.State} ${userAddress.ZipCode}`}</div>
                    <div style={{ marginBottom: '10px' }}>{`${userAddress.ThreeLetterISORegionName}`}</div>
                    {/* @ts-ignore */}
                    <Secondary onClick={() => saveButton.props.onClick(userAddress)}>{resources.userAddressButton}</Secondary>
                </div>
            </div>
        );
    } else if (defaultCountryRegionId === 'BRA') {
        return (
            <div>
                <BrazilCheckout customSetAddress={customSetAddress} resources={resources} setIsDisabled={setIsDisabled} />
                <div className='custom-button-group'>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {cancelButton && (
                            // @ts-ignore
                            <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                {/* @ts-ignore */}
                                {cancelButton.props.text}
                            </Ghost>
                        )}
                    </div>
                    <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {saveButton && (
                            <Secondary
                                style={{ width: '100%' }}
                                // @ts-ignore
                                onClick={() => saveButton.props.onClick(inputAddress, defaultCountryRegionId)}
                                disabled={isDisabled}
                            >
                                {/* @ts-ignore */}
                                {saveButton.props.text}
                            </Secondary>
                        )}
                    </div>
                </div>
            </div>
        );
    } else if (defaultCountryRegionId === 'GHA' || defaultCountryRegionId === 'CIV' || defaultCountryRegionId === 'NGA') {
        return (
            <div>
                <AfricaCheckout
                    customSetAddress={customSetAddress}
                    resources={resources}
                    context={context}
                    setIsVisible={setIsVisible}
                    setIsDisabled={setIsDisabled}
                    filteredItems={filteredItems}
                    setUnitName={setUnitName}
                    setStakeNameAddress={setStakeNameAddress}
                    defaultCountryRegionId={defaultCountryRegionId}
                />
                {isVisible && (
                    <div className='custom-button-group'>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {cancelButton && (
                                // @ts-ignore
                                <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                    {/* @ts-ignore */}
                                    {cancelButton.props.text}
                                </Ghost>
                            )}
                        </div>
                        <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {saveButton && (
                                <Secondary
                                    style={{ width: '100%' }}
                                    onClick={() =>
                                        // @ts-ignore
                                        saveButton.props.onClick(inputAddress, defaultCountryRegionId, unitName, stakeNameAddress)
                                    }
                                    disabled={isDisabled}
                                >
                                    {/* @ts-ignore */}
                                    {saveButton.props.text}
                                </Secondary>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    } else if (defaultCountryRegionId === 'PER') {
        let filteredStateList;
        filteredItems.map(item => {
            if (item.key === 'State') {
                const { input } = item;

                // @ts-ignore
                filteredStateList = input.props.displayData;
            }
        });
        return (
            <div>
                <PeruCheckout
                    customSetAddress={customSetAddress}
                    resources={resources}
                    context={context}
                    setIsVisible={setIsVisible}
                    setIsDisabled={setIsDisabled}
                    filteredItems={updatedStateList && updatedStateList.length !== 0 ? updatedStateList : filteredStateList}
                    defaultCountryRegionId={defaultCountryRegionId}
                />
                <div className='custom-button-group'>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {cancelButton && (
                            // @ts-ignore
                            <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                {/* @ts-ignore */}
                                {cancelButton.props.text}
                            </Ghost>
                        )}
                    </div>
                    <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {saveButton && (
                            <Secondary
                                style={{ width: '100%' }}
                                // @ts-ignore
                                onClick={() => saveButton.props.onClick(inputAddress, defaultCountryRegionId)}
                                disabled={isDisabled}
                            >
                                {/* @ts-ignore */}
                                {saveButton.props.text}
                            </Secondary>
                        )}
                    </div>
                </div>
            </div>
        );
    } else if (defaultCountryRegionId === 'URY') {
        let filteredStateList;
        filteredItems.map(item => {
            if (item.key === 'State') {
                const { input } = item;

                // @ts-ignore
                filteredStateList = input.props.displayData.filter((option: any) => {
                    return option.value !== '';
                });
            }
        });
        return (
            <div>
                <UruguayCheckout
                    customSetAddress={customSetAddress}
                    resources={resources}
                    context={context}
                    setIsVisible={setIsVisible}
                    setIsDisabled={setIsDisabled}
                    filteredItems={updatedStateList && updatedStateList.length !== 0 ? updatedStateList : filteredStateList}
                    defaultCountryRegionId={defaultCountryRegionId}
                    addressItems={props.items}
                />
                <div className='custom-button-group'>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {cancelButton && (
                            // @ts-ignore
                            <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                {/* @ts-ignore */}
                                {cancelButton.props.text}
                            </Ghost>
                        )}
                    </div>
                    <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {saveButton && (
                            <Secondary
                                style={{ width: '100%' }}
                                // @ts-ignore
                                onClick={() => saveButton.props.onClick(inputAddress, defaultCountryRegionId)}
                                disabled={isDisabled}
                            >
                                {/* @ts-ignore */}
                                {saveButton.props.text}
                            </Secondary>
                        )}
                    </div>
                </div>
            </div>
        );
    } else if (defaultCountryRegionId === 'CHL') {
        let filteredStateList: any;
        filteredItems.map((item: any) => {
            if (item.key === 'State') {
                // @ts-ignore
                filteredStateList = item.input.props.displayData.filter((option: any) => {
                    return stateCheckListChl.some(item => item.value === option.key && item.label === option.value);
                });
            }
        });

        return (
            <div>
                <ChileCheckout
                    customSetAddress={customSetAddress}
                    resources={resources}
                    context={context}
                    setIsVisible={setIsVisible}
                    setIsDisabled={setIsDisabled}
                    filteredItems={updatedStateList && updatedStateList.length !== 0 ? updatedStateList : filteredStateList}
                    defaultCountryRegionId={defaultCountryRegionId}
                />
                <div className='custom-button-group'>
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {cancelButton && (
                            // @ts-ignore
                            <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                {/* @ts-ignore */}
                                {cancelButton.props.text}
                            </Ghost>
                        )}
                    </div>
                    <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        {saveButton && (
                            <Secondary
                                style={{ width: '100%' }}
                                // @ts-ignore
                                onClick={() => saveButton.props.onClick(inputAddress, defaultCountryRegionId)}
                                disabled={isDisabled}
                            >
                                {/* @ts-ignore */}
                                {saveButton.props.text}
                            </Secondary>
                        )}
                    </div>
                </div>
            </div>
        );
    } else {
        return (
            <Node {...AddressForm}>
                {heading}
                {filteredItems.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    // @ts-ignore
                    if (input.props.id === 'shipping_addressthreeletterisoregionname') {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                <select
                                    //@ts-ignore
                                    {...input.props}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        border: '1px solid black',
                                        boxSizing: 'border-box !important',
                                        borderRadius: '2px',
                                        fontSize: '18px',
                                        lineHeight: '24px',
                                        fontWeight: 'normal',
                                        height: '48px'
                                    }}
                                    disabled
                                >
                                    {/* @ts-ignore */}
                                    {input.props.displayData.map((data: any) => {
                                        return (
                                            // @ts-ignore
                                            <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                {data.value}
                                            </option>
                                        );
                                    })}
                                </select>
                                {/* {input} */}
                            </Node>
                        );
                    } else if (item.key === 'State' && defaultCountryRegionId === 'KOR') {
                        let filteredStateList: any;
                        if (context.request.locale === 'en-US') {
                            // @ts-ignore
                            filteredStateList = input.props.displayData.filter((option: any) => {
                                return stateCheckList.some(item => item.value === option.key && item.label === option.value);
                            });
                        } else {
                            // @ts-ignore
                            filteredStateList = input.props.displayData.filter((option: any) => {
                                return stateCheckListKo.some(item => item.value === option.key && item.label === option.value);
                            });
                        }
                        // input.props.displayData;
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {updatedStateList && updatedStateList.length !== 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        // @ts-ignore
                                        onChange={e => customKoreaStateOnChange(input.props.onChange, e)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {updatedStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        // @ts-ignore
                                        onChange={e => customKoreaStateOnChange(input.props.onChange, e)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {filteredStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                                {/* {input} */}
                            </Node>
                        );
                    } else if (item.key === 'State' && defaultCountryRegionId === 'VNM') {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {updatedStateList && updatedStateList.length !== 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        // @ts-ignore
                                        onChange={e => customKoreaStateOnChange(input.props.onChange, e)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {updatedStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        // @ts-ignore
                                        onChange={e => customKoreaStateOnChange(input.props.onChange, e)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {input.props.displayData.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                                {/* {input} */}
                            </Node>
                        );
                    } else if (item.key === 'State' && defaultCountryRegionId === 'GTM') {
                        let filteredStateList: any;
                        // @ts-ignore
                        if (input && input.props && input.props.displayData) {
                            // @ts-ignore
                            filteredStateList = input.props.displayData.filter((option: any) => {
                                return option.value !== '';
                            });
                        }
                        // @ts-ignore
                        if (input && input.props && input.props.displayData) {
                            // @ts-ignore
                            input.props.displayData.map((item: any) => {
                                if (item.key === '') {
                                    const value = item.value.split(' ');
                                    value.pop();
                                    const popValue = value.join(' ');
                                    item.value = `${popValue} ${resources.addressDepartmentLabel}`;
                                }
                            });
                        }
                        // @ts-ignore
                        if (label && label.props && label.props.text) {
                            // @ts-ignore
                            label.props.text = `${resources.addressDepartmentLabel} *`;
                        }
                        // @ts-ignore
                        if (alert && alert.props && alert.props.message) {
                            // @ts-ignore
                            alert.props.message = resources.addressDepartmentRequiredErrorMessage;
                        }
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {updatedStateList && updatedStateList.length !== 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {updatedStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : filteredStateList && filteredStateList.length > 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {filteredStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    { input }
                                )}
                            </Node>
                        );
                    } else if (item.key === 'State' && defaultCountryRegionId === 'MEX') {
                        // @ts-ignore
                        if (label && label.props && label.props.text) {
                            // @ts-ignore
                            label.props.text = resources.addressStateProvinceLabel;
                        }
                        // @ts-ignore
                        if (alert && alert.props && alert.props.message) {
                            // @ts-ignore
                            alert.props.message = resources.addressStateProvinceRequiredErrorMessage;
                        }
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {updatedStateList && updatedStateList.length !== 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {updatedStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {input.props.displayData.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                            </Node>
                        );
                    } else if (item.key === 'DistrictName' && defaultCountryRegionId === 'MEX') {
                        // @ts-ignore
                        if (label && label.props && label.props.text) {
                            // @ts-ignore
                            label.props.text = resources.addressColoniaLabel;
                        }
                        // @ts-ignore
                        if (alert && alert.props && alert.props.message) {
                            // @ts-ignore
                            alert.props.message = resources.addressColoniaRequiredErrorMessage;
                        }
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {input}
                            </Node>
                        );
                    } else if (item.key === 'State') {
                        // @ts-ignore
                        const filteredStateList = input.props.displayData.filter((option: any) => {
                            return option.value !== '';
                        });
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {updatedStateList && updatedStateList.length !== 0 ? (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {updatedStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <select
                                        //@ts-ignore
                                        {...input.props}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            border: '1px solid black',
                                            boxSizing: 'border-box !important',
                                            borderRadius: '2px',
                                            fontSize: '18px',
                                            lineHeight: '24px',
                                            fontWeight: 'normal',
                                            height: '48px'
                                        }}
                                    >
                                        <option value={''} selected={true} disabled />
                                        {/* @ts-ignore */}
                                        {filteredStateList.map((data: any) => {
                                            return (
                                                // @ts-ignore
                                                <option value={data.key} key={data.key} selected={data.key === input.props.value}>
                                                    {data.value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                )}
                            </Node>
                        );
                    } else {
                        return (
                            <Node {...AddressItem} key={key}>
                                {label}
                                {/* {alert} */}
                                {/* @ts-ignore */}
                                {alert && alert.props.message && <label style={{ color: 'red' }}>{alert.props.message}</label>}
                                {input}
                            </Node>
                        );
                    }
                })}
                {hasError && (
                    <Node {...error.AddressError}>
                        {error.title}
                        {error.message}
                    </Node>
                )}
                {/* {defaultCountryRegionId === 'MEX' || defaultCountryRegionId === 'PAN' || defaultCountryRegionId === 'AUS' ? ( */}
                {defaultCountryRegionId === 'MEX' || defaultCountryRegionId === 'PAN' ? (
                    <div className='custom-button-group'>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {cancelButton && (
                                // @ts-ignore
                                <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                    {/* @ts-ignore */}
                                    {cancelButton.props.text}
                                </Ghost>
                            )}
                        </div>
                        <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {saveButton && (defaultCountryRegionId === 'USA' || defaultCountryRegionId === 'CAN') ? (
                                // @ts-ignore
                                <Secondary style={{ width: '100%' }} onClick={() => saveButton.props.onClick('', false)}>
                                    {/* @ts-ignore */}
                                    {saveButton.props.text}
                                </Secondary>
                            ) : saveButton ? (
                                // @ts-ignore
                                <Secondary style={{ width: '100%' }} onClick={() => saveButton.props.onClick()} disabled={disabled}>
                                    {/* @ts-ignore */}
                                    {saveButton.props.text}
                                </Secondary>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div className='custom-button-group'>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {cancelButton && (
                                // @ts-ignore
                                <Ghost style={{ width: '100%' }} onClick={() => cancelButton.props.onClick()}>
                                    {/* @ts-ignore */}
                                    {cancelButton.props.text}
                                </Ghost>
                            )}
                        </div>
                        <div className='save-button' style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {saveButton && (defaultCountryRegionId === 'USA' || defaultCountryRegionId === 'CAN') ? (
                                // @ts-ignore
                                <Secondary style={{ width: '100%' }} onClick={() => saveButton.props.onClick('', false)}>
                                    {/* @ts-ignore */}
                                    {saveButton.props.text}
                                </Secondary>
                            ) : saveButton ? (
                                // @ts-ignore
                                <Secondary style={{ width: '100%' }} onClick={() => saveButton.props.onClick()}>
                                    {/* @ts-ignore */}
                                    {saveButton.props.text}
                                </Secondary>
                            ) : null}
                        </div>
                    </div>
                )}
            </Node>
        );
    }
};

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const {
        CheckoutShippingAddress,
        checkoutErrorRef,
        alert,
        viewState,
        showAddress,
        showAddressSelect,
        showAddOrUpdateAddress,
        resources,
        showSmarty,
        smartyAddress,
        addUpdateAddress,
        loading,
        smartyFailed,
        countryRegionId
    } = props;

    return (
        <Module {...CheckoutShippingAddress} ref={checkoutErrorRef}>
            {alert}
            {!viewState.isShowAddOrUpdateAddress && !viewState.isShowAddresList && !viewState.isShowAddress && !loading && (
                <>
                    {/* @ts-ignore */}
                    <Secondary
                        style={{ width: '100%' }}
                        onClick={() =>
                            showAddressSelect &&
                            showAddressSelect.addButton &&
                            // @ts-ignore
                            showAddressSelect.addButton.props &&
                            // @ts-ignore
                            showAddressSelect.addButton.props.onClick
                                ? // @ts-ignore
                                  showAddressSelect.addButton.props.onClick()
                                : showAddressSelect &&
                                  showAddressSelect.addButton &&
                                  // @ts-ignore
                                  showAddressSelect.addButton.props &&
                                  // @ts-ignore
                                  showAddressSelect.addButton.props.children &&
                                  // @ts-ignore
                                  showAddressSelect.addButton.props.children.props &&
                                  // @ts-ignore
                                  showAddressSelect.addButton.props.children.props.onClick
                                ? // @ts-ignore
                                  showAddressSelect.addButton.props.children.props.onClick()
                                : ''
                        }
                    >
                        {/* @ts-ignore */}
                        {showAddressSelect.addButton.props.text
                            ? // @ts-ignore
                              showAddressSelect.addButton.props.text
                            : // @ts-ignore
                            showAddressSelect.addButton.props.children &&
                              // @ts-ignore
                              showAddressSelect.addButton.props.children.props &&
                              // @ts-ignore
                              showAddressSelect.addButton.props.children.props.text
                            ? // @ts-ignore
                              showAddressSelect.addButton.props.children.props.text
                            : 'Add Address'}
                    </Secondary>
                </>
            )}
            {viewState.isShowAddress && <AddressShow {...showAddress} defaultCountryRegionId={countryRegionId} context={props.context} />}
            {viewState.isShowAddresList && (
                <AddressSelect
                    {...showAddressSelect}
                    defaultCountryRegionId={countryRegionId}
                    resources={resources}
                    context={props.context}
                />
            )}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate
                    {...showAddOrUpdateAddress}
                    resources={resources}
                    context={props.context}
                    showSmarty={showSmarty}
                    smartyAddress={smartyAddress}
                    userAddress={addUpdateAddress}
                    loading={loading}
                    smartyFailed={smartyFailed}
                />
            )}
        </Module>
    );
};

export default CheckoutShippingAddressView;
